import { useEffect, useMemo, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Loader2, Download } from 'lucide-react'
import { downloadFile } from '@/lib/utils'

export interface AvatarPreviewProps {
  imageData?: string | null
  imageUrl?: string | null
  mimeType?: string
  isLoading?: boolean
  size?: number
  className?: string
  compact?: boolean
  alt?: string
  testId?: string
}

const SHUFFLE_AVATAR_IMAGES = [
  '/images/avatar-shuffle/01.jpg',
  '/images/avatar-shuffle/02.jpg',
  '/images/avatar-shuffle/03.jpg',
  '/images/avatar-shuffle/04.jpg',
  '/images/avatar-shuffle/05.jpg',
  '/images/avatar-shuffle/06.jpg',
  '/images/avatar-shuffle/07.jpg',
  '/images/avatar-shuffle/08.jpg',
  '/images/avatar-shuffle/09.jpg',
  '/images/avatar-shuffle/10.jpg',
  '/images/avatar-shuffle/11.jpg',
  '/images/avatar-shuffle/12.jpg',
  '/images/avatar-shuffle/13.jpg',
]

const SHUFFLE_INTERVAL_MS = 180

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function randomShuffleOrder(): number[] {
  const indices = SHUFFLE_AVATAR_IMAGES.map((_, i) => i)
  for (let i = indices.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[indices[i], indices[j]] = [indices[j], indices[i]]
  }
  return indices
}

export function AvatarPreview({
  imageData,
  imageUrl,
  mimeType = 'image/png',
  isLoading,
  size = 280,
  className,
  compact = false,
  alt = 'Avatar',
  testId,
}: AvatarPreviewProps) {
  const imageSrc = imageData ? `data:${mimeType};base64,${imageData}` : imageUrl || null
  const [shuffleIndex, setShuffleIndex] = useState(0)
  const shuffleOrderRef = useRef<number[]>(SHUFFLE_AVATAR_IMAGES.map((_, i) => i))

  // Preload only while the generator is visible; these are decorative fallbacks.
  useEffect(() => {
    if (!isLoading || typeof window === 'undefined') return
    SHUFFLE_AVATAR_IMAGES.forEach((src) => {
      const img = new window.Image()
      img.src = src
    })
  }, [isLoading])

  useEffect(() => {
    if (!isLoading || prefersReducedMotion()) return

    shuffleOrderRef.current = randomShuffleOrder()
    setShuffleIndex(0)
    let cursor = 0
    const id = window.setInterval(() => {
      cursor = (cursor + 1) % shuffleOrderRef.current.length
      setShuffleIndex(cursor)
    }, SHUFFLE_INTERVAL_MS)
    return () => window.clearInterval(id)
  }, [isLoading])

  const activeShuffleSrc = useMemo(
    () => SHUFFLE_AVATAR_IMAGES[shuffleOrderRef.current[shuffleIndex] ?? 0],
    [shuffleIndex],
  )

  const handleDownload = () => {
    if (!imageData) return
    const binary = atob(imageData)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const ext = mimeType.includes('png') ? 'png' : 'jpg'
    downloadFile(new Blob([bytes], { type: mimeType }), `avatar.${ext}`, mimeType)
  }

  return (
    <div
      className={cn(
        'group relative rounded-full overflow-hidden bg-gradient-to-br from-indigo-950 to-purple-900 flex items-center justify-center',
        className
      )}
      style={{ width: size, height: size }}
      data-testid={testId}
      aria-busy={isLoading}
    >
      {isLoading && (
        <>
          <img
            src="/images/avatar-silhouette.png"
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
            draggable={false}
          />
          <img
            key={activeShuffleSrc}
            src={activeShuffleSrc}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover animate-avatar-shuffle-in"
            draggable={false}
          />
          <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
          <div className="relative flex flex-col items-center gap-3">
            <Loader2 className={cn('animate-spin text-white drop-shadow-[0_0_8px_rgba(0,0,0,0.6)]', compact ? 'size-5' : 'size-8')} />
            {!compact && <span className="text-sm text-white drop-shadow-[0_0_4px_rgba(0,0,0,0.8)]">Gerando avatar...</span>}
          </div>
        </>
      )}

      {!isLoading && imageSrc && (
        <>
          <img
            src={imageSrc}
            alt={alt}
            className="w-full h-full object-cover"
            draggable={false}
          />
          {imageData && (
            <button
              type="button"
              onClick={handleDownload}
              className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors cursor-pointer"
            >
              <Download className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          )}
        </>
      )}

      {!isLoading && !imageSrc && (
        <img
          src="/images/avatar-silhouette.png"
          alt=""
          className="w-full h-full object-cover opacity-60"
          draggable={false}
        />
      )}
    </div>
  )
}
